import { Fragment, useMemo, useState } from "react";
import { api, useLoad } from "../api";
import { CATEGORY_LABEL, fmt } from "../lib/format";
import { linkHandler } from "../lib/router";
import { SelectField, Switch } from "../components/ui";
import { ConventionsNote, CostSection, Limitations, StepsSection, YearCharts, YearsTable } from "../components/BacktestYears";
import type { BacktestGroup, BacktestMarket, BacktestStats, BacktestTrade, BacktestVersion, Num } from "../types";

type SortKey = "perYearPct" | "sumPct" | "winRate" | "trades" | "profitFactor" | "avgPct" | "maxDrawdownPct";

const SORT_LABEL: Record<SortKey, string> = {
  perYearPct: "результату за год",
  sumPct: "итогу за всю историю",
  winRate: "доле прибыльных",
  profitFactor: "отношению прибыли к убыткам",
  avgPct: "средней сделке",
  trades: "числу сделок",
  maxDrawdownPct: "просадке (меньше — лучше)",
};

const val = (m: BacktestMarket, k: SortKey): number | null =>
  k === "perYearPct" ? m.perYearPct : (m.stats[k] as number | null);

/** Сводный бэктест канона Уильямса по всем рынкам: где методика сработала лучше всего, все сделки по каждому рынку. */
export function BacktestPage() {
  const { data, error, loading } = useLoad((s) => api.backtest(s), []);
  const [sort, setSort] = useState<SortKey>("perYearPct");
  const [onlyReliable, setOnlyReliable] = useState(true);
  const [open, setOpen] = useState<string | null>(null);

  const markets = useMemo(() => {
    const list = (data?.markets ?? []).filter((m) => m.stats.trades > 0 && (!onlyReliable || m.reliable));
    const v = (m: BacktestMarket) => {
      const x = val(m, sort);
      return x == null ? -Infinity : sort === "maxDrawdownPct" ? -x : x;
    };
    return [...list].sort((a, b) => v(b) - v(a));
  }, [data, sort, onlyReliable]);
  const noTrades = (data?.markets ?? []).filter((m) => m.stats.trades === 0).length;
  const few = (data?.markets ?? []).filter((m) => m.stats.trades > 0 && !m.reliable).length;

  return (
    <main className="page journal-page backtest-page">
      <a className="back" href="/ru" onClick={linkHandler("/ru")}>
        ← Все рынки
      </a>
      <h1>Бэктест по всем рынкам</h1>
      <p className="lede">
        Правила сделок по умолчанию — «Сетап 4+ · 3 ATR»: вход по полному сетапу из индикаторов Уильямса. Ниже — его
        итог; канон с входом по линии тренда — в сравнении наборов.
      </p>
      {loading && !data && <div className="skeleton">Считаем сделки всех рынков…</div>}
      {error && <div className="notice notice-error">Не удалось загрузить бэктест: {error}</div>}
      {data && (
        <>
          <details className="pane-help bt-howto" open>
            <summary>Как читать эти цифры</summary>
            <div className="pane-help-body">
              <p>
                <b>Результат сделки</b> — сколько процентов от цены контракта она заработала или потеряла,{" "}
                <b>после издержек {(data.costPct ?? 0).toFixed(2).replace(".", ",")} % туда-обратно</b> (допущение: комиссия и
                проскальзывание). Пример: продажа по 4 470 и выход по 3 925 — +545 пунктов, или +12,2 % от цены входа до
                издержек.
              </p>
              <p>
                <b>«В год»</b> — сумма % сделок рынка, делённая на число лет истории. Это сумма доходностей сделок одного
                контракта без плеча, а не доходность портфеля: модели капитала в системе нет. Колонка «Итог» — та же сумма
                за всю историю.
              </p>
              <p>
                <b>Прибыльных 25–35 % — это нормально для этой методики.</b> Она трендовая: много маленьких убытков по
                стопу (обычно −1…−5 %) и редкие большие прибыли (+10…+50 %), которые всё перекрывают. Поэтому смотрите
                на «Прибыль / убытки»: больше 1 — рынок в плюсе, 2 и выше — хорошо. Медианная сделка при этом
                отрицательная — так и должно быть.
              </p>
              <p>
                <b>Почти все выходы — «стоп».</b> Цели у методики нет: и прибыльные сделки закрывает подтянутый
                сопровождающий стоп. Причина «стоп» не означает убыток.
              </p>
              <p className="muted">
                {data.method} Расчёт на {data.generated}.
              </p>
            </div>
          </details>

          {data.conventions && <ConventionsNote data={data} />}
          {data.limitations && <Limitations items={data.limitations} />}

          <section>
            <h2>Итог (после издержек)</h2>
            <GroupTiles g={data.total} />
          </section>

          {data.years && data.years.length > 0 && (
            <section>
              <h2>Результаты по годам</h2>
              <p className="lede">
                Год сделки — год открытия; в статистику входят закрытые сделки. Открытые на 31 декабря и
                перешедшие из прошлого года — отдельными строками, календарный результат (mark-to-market) — отдельно, не
                смешивается с результатом по году открытия. * — неполный год.
              </p>
              <YearsTable years={data.years} />
            </section>
          )}

          {data.years && data.years.length > 0 && <YearCharts years={data.years} />}
          {data.versions && data.versions.length > 1 && <VersionsSection versions={data.versions} />}
          {data.steps && data.steps.length > 0 && <StepsSection steps={data.steps} />}
          {data.costs && data.costs.length > 0 && <CostSection costs={data.costs} current={data.costPct} />}

          <section>
            <h2>По группам инструментов</h2>
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="text">Группа</th>
                    <th>Рынков в плюсе</th>
                    <th>Средний рынок в год</th>
                    <th>Сделок</th>
                    <th>Прибыльных</th>
                    <th>Средняя сделка</th>
                    <th>Ср. прибыль</th>
                    <th>Ср. убыток</th>
                    <th title="Сумма прибылей / сумма убытков">Прибыль / убытки</th>
                  </tr>
                </thead>
                <tbody>
                  {data.categories.map((c) => (
                    <tr key={c.name}>
                      <td className="text">{CATEGORY_LABEL[c.name] ?? c.name}</td>
                      <td>
                        {c.marketsPositive} из {c.marketsWithTrades}
                      </td>
                      <td className={tone(c.avgPerYearPct)}>
                        <b>{signedPct(c.avgPerYearPct)}</b>
                      </td>
                      <td>{c.stats.trades}</td>
                      <td>{fmt.pct(c.stats.winRate)}</td>
                      <td className={tone(c.stats.avgPct)}>{signedPct(c.stats.avgPct)}</td>
                      <td className="up">{signedPct(c.stats.avgWinPct)}</td>
                      <td className="down">{signedPct(c.stats.avgLossPct)}</td>
                      <td className={tone(c.stats.profitFactor == null ? null : c.stats.profitFactor - 1)}>{pf(c.stats.profitFactor)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <div className="section-head">
              <h2>По инструментам</h2>
              <span className="h-count">{markets.length} рынков в таблице</span>
            </div>
            <div className="filter-bar" role="group" aria-label="Сортировка и фильтр рынков">
              <SelectField
                label="Сортировать по"
                className="select-wide"
                value={sort}
                onChange={setSort}
                options={(Object.entries(SORT_LABEL) as [SortKey, string][]).map(([k, v]) => [k, v.charAt(0).toUpperCase() + v.slice(1)])}
              />
              <Switch
                checked={onlyReliable}
                onChange={setOnlyReliable}
                label="Только рынки с 5+ сделками"
                description="Остальные — со случайной статистикой"
                meta={<span className={`count-badge${onlyReliable ? "" : " is-shown"}`}>{onlyReliable ? `Скрыто: ${few}` : `Показано с малой выборкой: ${few}`}</span>}
              />
            </div>
            <div className="explain">
              <p>Нажмите на строку — откроются все сделки рынка.</p>
              <p>
                Если сделок меньше пяти, статистика рынка случайна: одна удачная или неудачная сделка меняет её целиком.
                Поэтому такие рынки по умолчанию скрыты.
              </p>
              {noTrades > 0 && <p>Без единой сделки за историю: {noTrades} рынков — их в таблице нет.</p>}
            </div>
            <div className="table-scroll">
              <table className="data-table bt-table">
                <thead>
                  <tr>
                    <th className="text">#</th>
                    <th className="text">Инструмент</th>
                    <th>Лет</th>
                    <th>В год</th>
                    <StatHead />
                    <th>Покупки</th>
                    <th>Продажи</th>
                  </tr>
                </thead>
                <tbody>
                  {markets.map((m, i) => (
                    <Fragment key={m.code}>
                      <tr
                        className={`bt-row ${open === m.code ? "is-open" : ""} ${m.reliable ? "" : "is-few"}`}
                        onClick={() => setOpen(open === m.code ? null : m.code)}
                        tabIndex={0}
                        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setOpen(open === m.code ? null : m.code))}
                        aria-expanded={open === m.code}
                      >
                        <td className="text muted">{i + 1}</td>
                        <td className="text">
                          <b>{m.name}</b>{" "}
                          <span className="muted">
                            {m.code} · {CATEGORY_LABEL[m.category] ?? m.category}
                          </span>
                        </td>
                        <td>{m.years.toFixed(1).replace(".", ",")}</td>
                        <td className={tone(m.perYearPct)}>
                          <b>{signedPct(m.perYearPct)}</b>
                        </td>
                        <StatCells s={m.stats} />
                        <td>{side(m.stats.longs, m.stats.longWins, m.stats.longSumPct)}</td>
                        <td>{side(m.stats.shorts, m.stats.shortWins, m.stats.shortSumPct)}</td>
                      </tr>
                      {open === m.code && (
                        <tr className="bt-detail">
                          <td colSpan={16}>
                            <TradeList m={m} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </main>
  );
}

function num(v: Num) {
  return v == null ? "—" : v.toFixed(1).replace(".", ",");
}

function GroupTiles({ g }: { g: BacktestGroup }) {
  const s = g.stats;
  const tiles: [string, string, string?][] = [
    ["Рынков в плюсе", `${g.marketsPositive} из ${g.marketsWithTrades}`, `ещё ${g.markets - g.marketsWithTrades} без сделок`],
    ["Средний рынок в год", signedPct(g.avgPerYearPct), "сумма % сделок рынка в год — не доходность портфеля"],
    ["Сделок", String(s.trades)],
    ["Прибыльных", fmt.pct(s.winRate), `${s.wins} из ${s.trades}${s.zeros ? `, нулевых ${s.zeros}` : ""}`],
    ["Средняя / медианная сделка", `${signedPct(s.avgPct)} / ${signedPct(g.medianPct)}`],
    ["Средняя прибыль / убыток", `${signedPct(s.avgWinPct)} / ${signedPct(s.avgLossPct)}`],
    ["Прибыль / убытки", pf(s.profitFactor), `до издержек ${pf(s.profitFactorGross ?? null)}; 2024 — апр. 2025: ${pf(g.firstHalf.profitFactor)}, с мая 2025: ${pf(g.secondHalf.profitFactor)}`],
    ["Expectancy", signedPct(s.expectancyPct ?? null), "средняя закрытая сделка после издержек"],
    ["Сделок на рынок в год", num(g.tradesPerMarketYear)],
    ["Просадка рынка", g.ddMedianPct == null ? "—" : `−${num(g.ddMedianPct)}%`, g.ddP90Pct == null ? undefined : `медиана; у худших 10 % рынков −${num(g.ddP90Pct)}%`],
    ["Покупки", side(s.longs, s.longWins, s.longSumPct / Math.max(1, g.marketsWithTrades), true)],
    ["Продажи", side(s.shorts, s.shortWins, s.shortSumPct / Math.max(1, g.marketsWithTrades), true)],
  ];
  return (
    <dl className="bt-tiles">
      {tiles.map(([k, v, note]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
          {note && <dd className="muted">{note}</dd>}
        </div>
      ))}
    </dl>
  );
}

function StatHead() {
  return (
    <>
      <th>Сделок</th>
      <th>Прибыльных</th>
      <th title="Сумма % всех сделок за всю историю">Итог</th>
      <th>Средняя</th>
      <th>Ср. прибыль</th>
      <th>Ср. убыток</th>
      <th title="Сумма прибылей / сумма убытков">Приб./уб.</th>
      <th>Просадка</th>
      <th>Лучшая</th>
      <th>Худшая</th>
    </>
  );
}

function StatCells({ s }: { s: BacktestStats }) {
  return (
    <>
      <td>{s.trades}</td>
      <td className={tone(s.winRate == null ? null : s.winRate - 50)}>{fmt.pct(s.winRate)}</td>
      <td className={tone(s.sumPct)}>
        <b>{signedPct(s.sumPct)}</b>
      </td>
      <td className={tone(s.avgPct)}>{signedPct(s.avgPct)}</td>
      <td className="up">{signedPct(s.avgWinPct)}</td>
      <td className="down">{signedPct(s.avgLossPct)}</td>
      <td className={tone(s.profitFactor == null ? null : s.profitFactor - 1)}>{pf(s.profitFactor)}</td>
      <td>{s.trades ? `−${fmt.price(s.maxDrawdownPct)}%` : "—"}</td>
      <td className="up">{signedPct(s.bestPct)}</td>
      <td className="down">{signedPct(s.worstPct)}</td>
    </>
  );
}

function TradeList({ m }: { m: BacktestMarket }) {
  const rows: (BacktestTrade & { isOpen?: boolean })[] = [...m.trades].reverse();
  if (m.open) rows.unshift({ ...m.open, isOpen: true });
  return (
    <div className="bt-trades">
      <p className="muted">
        Данные с {fmt.date(m.dataFrom)} по {fmt.date(m.dataTo)}.{" "}
        <a href={`/ru/${encodeURIComponent(m.code)}`} onClick={linkHandler(`/ru/${encodeURIComponent(m.code)}`)}>
          Открыть график {m.code}
        </a>
        {m.stats.rubTotal != null && <> · итог на 1 контракт после издержек: {fmt.signed(m.stats.rubTotal)} ₽</>}
      </p>
      <table className="data-table">
        <thead>
          <tr>
            <th className="text">Вход</th>
            <th className="text">Сторона</th>
            <th>Цена</th>
            <th className="text">Выход</th>
            <th>Цена</th>
            <th className="text">Причина</th>
            <th>Дней</th>
            <th>Пункты</th>
            <th title="До издержек">% до изд.</th>
            <th title="После издержек">% после</th>
            <th>₽ после изд.</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t, i) => (
            <tr key={i}>
              <td className="text">{fmt.date(t.entryDate)}</td>
              <td className="text">
                {t.dir > 0 ? "покупка" : "продажа"}
                {t.reentry ? " (перевход)" : ""}
              </td>
              <td>{fmt.price(t.entryPrice)}</td>
              <td className="text">{t.isOpen ? "открыта" : fmt.date(t.exitDate)}</td>
              <td>{fmt.price(t.exitPrice)}</td>
              <td className="text">{t.isOpen ? "по последней цене" : t.reason}</td>
              <td>{t.days}</td>
              <td className={tone(t.points)}>{fmt.signed(t.points)}</td>
              <td className={tone(t.pct)}>{signedPct(t.pct)}</td>
              <td className={tone(t.pctNet ?? t.pct)}>
                <b>{signedPct(t.pctNet ?? t.pct)}</b>
              </td>
              <td className={tone(t.rubNet ?? t.rub)}>{(t.rubNet ?? t.rub) == null ? "—" : fmt.signed((t.rubNet ?? t.rub) as number)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Сравнение наборов правил на тех же данных и в тех же условиях исполнения (после издержек). */
function VersionsSection({ versions }: { versions: BacktestVersion[] }) {
  const years = Array.from(new Set(versions.flatMap((v) => v.years.map((y) => y.year)))).sort();
  return (
    <section>
      <h2>Сравнение наборов правил</h2>
      <p className="lede">Те же рынки, период и исполнение. Прибыль / убытки по годам — по сделкам, открытым в году.</p>
      <div className="table-scroll">
        <table className="data-table bt-table">
          <thead>
            <tr>
              <th className="text">Правила</th>
              <th>Сделок</th>
              <th>Прибыльных</th>
              <th title="Сумма прибылей / сумма убытков">Прибыль / убытки</th>
              <th>Средняя сделка</th>
              {years.map((y) => (
                <th key={y}>{y}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {versions.map((v) => {
              const s = v.total.stats;
              return (
                <tr key={v.name} className={v.current ? "bt-current" : ""}>
                  <td className="text">
                    {v.current ? <b>{v.title}</b> : v.title}
                    {v.current && <span className="muted"> · по умолчанию</span>}
                  </td>
                  <td>{s.trades}</td>
                  <td>{fmt.pct(s.winRate)}</td>
                  <td className={tone(s.profitFactor == null ? null : s.profitFactor - 1)}>
                    <b>{pf(s.profitFactor)}</b>
                  </td>
                  <td className={tone(s.avgPct)}>{signedPct(s.avgPct)}</td>
                  {years.map((y) => {
                    const yr = v.years.find((x) => x.year === y);
                    const p = yr?.realized.profitFactor ?? null;
                    return (
                      <td key={y} className={tone(p == null ? null : p - 1)}>
                        {pf(p)}
                        {yr?.partial ? "*" : ""}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function signedPct(v: Num) {
  return v == null ? "—" : `${v > 0 ? "+" : ""}${v.toFixed(2).replace(".", ",")}%`;
}

function pf(v: Num) {
  return v == null ? "без убытков" : v.toFixed(2).replace(".", ",");
}

function tone(v: Num) {
  return v == null || v === 0 ? "" : v > 0 ? "up" : "down";
}

function side(n: number, wins: number, sum: number, perMarket = false) {
  if (!n) return "—";
  return `${n} (${Math.round((wins / n) * 100)}% приб.)${perMarket ? "" : ` ${signedPct(sum)}`}`;
}
